import { useEffect, useId, useRef, type ReactNode } from 'react'

/** Modal shell for the points flow (Figma 2362:4446 / 2260:369): a 35% ink scrim over the panel
 * behind it, with a white rounded card centred on top.
 *
 * A native `<dialog>` opened with `showModal()`, so the browser supplies the focus trap, the
 * scroll lock, Escape-to-dismiss and the `::backdrop` scrim — all of which a hand-rolled
 * `role="dialog"` div has to reimplement, usually incompletely. At a till that matters: the
 * cashier must not be able to tab into the marketplace behind an open claim confirmation and
 * spend points from a control they can't see.
 *
 * Always carries an accessible name via `aria-labelledby`, so a dialog can't ship announced as
 * just "dialog".
 */
export function Dialog({
  title,
  titleHidden = false,
  onDismiss,
  width = 952,
  children,
}: {
  title: string
  /** Set when the design shows no visible heading; the name is still exposed to screen readers. */
  titleHidden?: boolean
  onDismiss: () => void
  width?: number
  children: ReactNode
}) {
  const titleId = useId()
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    // `showModal` is what puts the dialog in the top layer and turns on the browser's modal
    // behaviour; simply rendering the element does not.
    if (!el.open) el.showModal()
    return () => el.close()
  }, [])

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      // `cancel`, not `close`: cancel fires only for Escape, whereas close also fires from the
      // unmount cleanup below — which would re-run the caller's dismissal after it had already
      // torn the dialog down.
      onCancel={onDismiss}
      style={{ maxWidth: width }}
      className="m-auto w-full rounded-[10px] border-0 bg-white p-10 backdrop:bg-ink/35"
    >
      <h2
        id={titleId}
        className={titleHidden ? 'sr-only' : 'text-[28px] font-semibold leading-none text-ink'}
      >
        {title}
      </h2>
      {children}
    </dialog>
  )
}
